import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiX, FiStar, FiMail, FiMessageSquare, FiTrendingUp, FiTarget } = FiIcons;

const PricingPage = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const plans = [
    {
      name: 'Starter',
      price: billingPeriod === 'monthly' ? 29 : 290,
      period: billingPeriod === 'monthly' ? '/month' : '/year',
      description: 'Perfect for solo practitioners getting started',
      features: [
        '1 service listing',
        'Basic profile visibility',
        'Email notifications',
        'Standard support',
        'Client matching',
        'Up to 5 leads/month'
      ],
      limitations: [
        'No featured placement',
        'No WhatsApp integration',
        'Limited analytics'
      ],
      cta: 'Start Free Trial',
      popular: false
    },
    {
      name: 'Professional',
      price: billingPeriod === 'monthly' ? 49 : 490,
      period: billingPeriod === 'monthly' ? '/month' : '/year',
      description: 'Most popular for growing law practices',
      features: [
        '3 service listings',
        'Featured listing placement',
        'Priority lead notifications',
        'Email & phone support',
        'Advanced client matching',
        'Up to 15 leads/month',
        'Basic analytics dashboard',
        'Profile badge'
      ],
      limitations: [
        'No WhatsApp integration',
        'Limited premium features'
      ],
      cta: 'Start Free Trial',
      popular: true
    },
    {
      name: 'Premium',
      price: billingPeriod === 'monthly' ? 99 : 990,
      period: billingPeriod === 'monthly' ? '/month' : '/year',
      description: 'For established firms wanting maximum visibility',
      features: [
        'Unlimited service listings',
        'Top featured placement',
        'Instant lead notifications',
        'Priority support & account manager',
        'AI-powered client matching',
        'Unlimited leads',
        'WhatsApp integration',
        'Advanced analytics & reporting',
        'Premium profile badge',
        'Custom consultation rates',
        'Lead quality scoring'
      ],
      limitations: [],
      cta: 'Start Free Trial',
      popular: false
    }
  ];

  const faqs = [
    {
      question: 'How does the matching system work?',
      answer: 'Our AI analyzes client requests and matches them with lawyers based on practice area, location, experience, and availability. You only receive leads that match your expertise.'
    },
    {
      question: 'Do you take a commission from my deals?',
      answer: 'No, we never take a commission. You keep 100% of what you charge your clients. Our revenue comes only from the monthly subscription fees.'
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees.'
    },
    {
      question: 'What kind of leads can I expect?',
      answer: 'All leads are pre-screened for legitimacy. Clients provide their budget, timeline, and specific legal needs before being matched with lawyers.'
    },
    {
      question: 'Is there a free trial?',
      answer: 'Yes, all plans come with a 14-day free trial. You can test the platform and see the quality of leads before committing.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Simple, Transparent Pricing
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto mb-8"
          >
            Choose the plan that fits your practice. All plans include our AI-powered client matching system.
          </motion.p>

          {/* Billing Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex items-center justify-center mb-12"
          >
            <span className={`mr-3 ${billingPeriod === 'monthly' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                billingPeriod === 'yearly' ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingPeriod === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`ml-3 ${billingPeriod === 'yearly' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              Yearly
            </span>
            {billingPeriod === 'yearly' && (
              <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                Save 17%
              </span>
            )}
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className={`pricing-card bg-white p-8 rounded-lg shadow-lg relative ${
                  plan.popular ? 'featured-plan ring-2 ring-blue-500' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center">
                      <SafeIcon icon={FiStar} className="w-4 h-4 mr-1" />
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                    {plan.name}
                  </h3>
                  <p className={`mb-4 ${plan.popular ? 'text-blue-100' : 'text-gray-600'}`}>
                    {plan.description}
                  </p>
                  <div className="flex items-center justify-center">
                    <span className={`text-5xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                      ${plan.price}
                    </span>
                    <span className={`ml-2 ${plan.popular ? 'text-blue-100' : 'text-gray-600'}`}>
                      {plan.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <SafeIcon
                        icon={FiCheck}
                        className={`w-5 h-5 mr-3 ${plan.popular ? 'text-green-300' : 'text-green-500'}`}
                      />
                      <span className={plan.popular ? 'text-blue-100' : 'text-gray-700'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                  {plan.limitations.map((limitation, idx) => (
                    <li key={idx} className="flex items-center">
                      <SafeIcon
                        icon={FiX}
                        className={`w-5 h-5 mr-3 ${plan.popular ? 'text-red-300' : 'text-red-400'}`}
                      />
                      <span className={`${plan.popular ? 'text-blue-200' : 'text-gray-500'} line-through`}>
                        {limitation}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  className={`w-full py-4 rounded-lg font-semibold text-lg transition-all ${
                    plan.popular
                      ? 'bg-white text-blue-700 hover:bg-gray-50'
                      : 'bg-blue-700 text-white hover:bg-blue-800'
                  }`}
                >
                  {plan.cta}
                </button>

                <p className={`text-center text-sm mt-4 ${plan.popular ? 'text-blue-100' : 'text-gray-500'}`}>
                  14-day free trial • No setup fees
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              All Plans Include
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Core features that every lawyer gets, regardless of plan
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 text-blue-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTarget} className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">AI Matching</h3>
              <p className="text-gray-600 text-sm">Smart client-lawyer matching based on expertise</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 text-green-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiMail} className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Lead Notifications</h3>
              <p className="text-gray-600 text-sm">Instant email alerts for new client requests</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 text-purple-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTrendingUp} className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Performance Tracking</h3>
              <p className="text-gray-600 text-sm">Monitor your leads, responses, and conversions</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 text-orange-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiMessageSquare} className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Client Communication</h3>
              <p className="text-gray-600 text-sm">Direct messaging with potential clients</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our pricing and platform
            </p>
          </div>

          <div className="space-y-8">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-sm"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Getting Quality Leads?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Join hundreds of lawyers who are growing their practice with LegalCoupon
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button className="bg-white text-blue-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-all transform hover:scale-105">
              Start 14-Day Free Trial
            </button>
            <a
              href="mailto:support@legalcoupon.com"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-700 transition-all"
            >
              Talk to Sales
            </a>
          </div>
          <p className="text-sm text-blue-100 mt-6">
            No credit card required • Cancel anytime • Full support included
          </p>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;